import { z } from 'zod';
import type { OwnerScope } from '@prisma/client';
import { tagGroupSelectionModeSchema, tagScopeSchema } from './tag';
import type { TagGroupSelectionMode } from './tag';
import type { ConfigChangeSummary } from './provenance-summary.js';

export const tagGroupCreateSchema = z.object({
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).optional().nullable(),
  scope: tagScopeSchema.default('PRIVATE'),
  selectionMode: tagGroupSelectionModeSchema.default('SINGLE'),
  groupOrder: z.number().int().optional().nullable(),
}).strict();

export type TagGroupCreateInput = z.infer<typeof tagGroupCreateSchema>;

export const tagGroupUpdateSchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).optional().nullable(),
  selectionMode: tagGroupSelectionModeSchema.optional(),
  groupOrder: z.number().int().optional().nullable(),
}).strict().refine((input) => {
  return input.name !== undefined
    || input.description !== undefined
    || input.selectionMode !== undefined
    || input.groupOrder !== undefined;
}, {
  message: 'At least one field must be provided',
});

export type TagGroupUpdateInput = z.infer<typeof tagGroupUpdateSchema>;

export const tagGroupReorderSchema = z.object({
  groupIds: z.array(z.number().int().positive()).superRefine((groupIds, ctx) => {
    if (new Set(groupIds).size !== groupIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'groupIds must not contain duplicate IDs',
      });
    }
  }),
}).strict();

export type TagGroupReorderInput = z.infer<typeof tagGroupReorderSchema>;

export interface TagGroup {
  id: number;
  createdByMemberId: number;
  ownerMemberId: number | null;
  hubId: number;
  name: string;
  description?: string;
  ownerScope: OwnerScope;
  selectionMode: TagGroupSelectionMode;
  groupOrder?: number;
  createdAt: string;
  updatedAt: string;
  lastChange?: ConfigChangeSummary | null;
}
